import * as React from 'react'
import { UseSeparatedCountdownOptions, useSeparatedCountdownProps } from './useSeparatedCountdown.types';

/**
 * A custom hook for a countdown timer that separates the time.
 *
 * @param {number} durationInSeconds - The duration of the countdown in seconds.
 * @param {UseSeparatedCountdownOptions} [options] - Optional configuration options for the countdown.
 * @returns {useSeparatedCountdownProps} - An object containing the separated countdown, and functions to start, stop, and isRunning the countdown.
 *
 * @example
 * const { countdown, start, stop, isRunning } = useSeparatedCountdown(100);
 */
function useSeparatedCountdown(durationInSeconds: number, options?:UseSeparatedCountdownOptions): useSeparatedCountdownProps {
  const { interval = 1000, startOnMount = false } = options || {};

  const countdownRef = React.useRef(0);
  const [countdown, setCountdown] = React.useState(0);

  const start = React.useCallback(() => {
    countdownRef.current = durationInSeconds;
    setCountdown(durationInSeconds);
  }, [durationInSeconds]);

  const stop = React.useCallback(() => {
		countdownRef.current = 0;
		setCountdown(0);
	}, []);

  React.useEffect(() => {
    if (startOnMount) start();
  }, [start, startOnMount]);

  React.useEffect(() => {
    if (countdownRef.current > 0) {
      const timer = setTimeout(() => {
        countdownRef.current -= 1;
        setCountdown(countdownRef.current);
      }, interval);

      return () => clearTimeout(timer);
    }
  }, [countdown, interval]);

  return {
    start,
    stop,
    countdown,
    isRunning: countdown > 0,
  };
}

export default useSeparatedCountdown;
